import React, { useState, useMemo, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, ToggleButtonGroup, ToggleButton, TableFooter, Tooltip as MuiTooltip, useTheme, useMediaQuery } from '@mui/material';
import { useTranslate, useNotify } from 'react-admin';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatCurrency, formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';
import { RevenueSplit } from '../RevenueSplit';
import { reportMethodName } from '../methodName';

interface RevenueSummary {
  total_revenue: number;
  charged_revenue: number;
  vendor_billed_revenue: number;
  total_sessions: number;
  charged_sessions: number;
  avg_revenue_per_session: number;
  total_energy_kwh: number;
  total_energy_cost: number;
  total_idle_cost: number;
}

interface TimeSeriesData {
  date: string;
  revenue: number;
  sessions: number;
  charged_sessions: number;
  energy_kwh: number;
  energy_cost: number;
  idle_cost: number;
}

interface SiteBreakdown {
  site_id: string;
  site_name: string;
  revenue: number;
  sessions: number;
  charged_sessions: number;
}

interface MethodBreakdown {
  method: string;
  revenue: number;
  sessions: number;
  charged_sessions: number;
}

interface RevenueReportData {
  summary: RevenueSummary;
  time_series: TimeSeriesData[];
  breakdown_by_site: SiteBreakdown[];
  breakdown_by_method: MethodBreakdown[];
}

const COLORS = ['#009A75', '#2196F3', '#FFB800', '#FF6B6B', '#9C27B0', '#00BCD4'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

type Order = 'asc' | 'desc';

export const RevenueAnalysisReport: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [chargerId, setChargerId] = useState('');

  // Filter options state
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [chargers, setChargers] = useState<Array<{ id: string; name: string }>>([]);

  // Period type state (day/month)
  const [periodType, setPeriodType] = useState<'day' | 'month'>('day');

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof TimeSeriesData>('date');
  const [order, setOrder] = useState<Order>('desc');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePeriodTypeChange = (event: React.MouseEvent<HTMLElement>, newPeriodType: 'day' | 'month' | null) => {
    if (newPeriodType !== null) {
      setPeriodType(newPeriodType);
    }
  };

  const handleRequestSort = (property: keyof TimeSeriesData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch sites when vendor changes
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with vendor_id parameter if vendor is selected
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;

        const response = await fetch(url, { headers });
        const data = await response.json();
        setSites(data.data || []);

        // Reset site selection if it's no longer in the filtered list
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, [vendorId]); // Re-fetch when vendor changes

  // Fetch chargers when vendor or site changes
  useEffect(() => {
    const fetchChargers = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with appropriate filter parameters
        let url = `${API_CONFIG.baseURL}/api/backend/reports/filters/chargers`;
        const params = new URLSearchParams();

        if (siteId) {
          params.append('site_id', siteId);
        } else if (vendorId) {
          params.append('vendor_id', vendorId);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, { headers });
        const data = await response.json();
        setChargers(data.data || []);

        // Reset charger selection if it's no longer in the filtered list
        if (chargerId && !data.data?.some((c: any) => c.id === chargerId)) {
          setChargerId('');
        }
      } catch (error) {
        console.error('Error fetching chargers:', error);
      }
    };

    fetchChargers();
  }, [vendorId, siteId]); // Re-fetch when vendor or site changes

  // Fetch data
  const { data, loading, error, refetch } = useReportData<RevenueReportData>({
    endpoint: '/api/backend/reports/revenue',
    filters: {
      start_date: startDate,
      end_date: endDate,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
      ...(chargerId && { charger_id: chargerId }),
    },
  });

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    // Surface "no data yet" via toast rather than silently no-op'ing — the
    // user clicked Export and deserves feedback. The hook will fire its own
    // success/empty/failure toast based on the rows passed in.
    if (!data?.time_series || data.time_series.length === 0) {
      notify(
        translate('reports.export.noData', { fallback: 'No data to export — adjust filters and try again.' }),
        { type: 'warning' },
      );
      return;
    }

    exportToCSV(
      data.time_series,
      'revenue-analysis',
      [
        { key: 'date', label: 'Date', format: 'date' },
        ...(canSeeTotalSessions ? [{ key: 'sessions', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_sessions', label: 'Charged Sessions', format: 'number' },
        { key: 'energy_kwh', label: 'Energy (kWh)', format: 'number' },
        { key: 'energy_cost', label: 'Energy Cost', format: 'currency' },
        { key: 'idle_cost', label: 'Idle Cost', format: 'currency' },
        { key: 'revenue', label: 'Revenue', format: 'currency' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data || !data.time_series || data.time_series.length === 0) {
      notify(
        translate('reports.export.noData', { fallback: 'No data to export — adjust filters and try again.' }),
        { type: 'warning' },
      );
      return;
    }

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (siteId) {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        filters.site = { id: siteId, name: selectedSite.name };
      }
    }

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    if (chargerId) {
      const selectedCharger = chargers.find(c => c.id === chargerId);
      if (selectedCharger) {
        filters.charger = { id: chargerId, name: selectedCharger.name };
      }
    }

    await exportToExcel(
      data.time_series,
      'revenue-analysis',
      {
        columns: [
          { key: 'date', label: translate('reports.table.date', { fallback: 'Date' }), format: 'date' },
          ...(canSeeTotalSessions ? [{ key: 'sessions', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_sessions', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
          { key: 'energy_kwh', label: translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' }), format: 'number' },
          { key: 'energy_cost', label: translate('reports.table.energyCost', { fallback: 'Energy Cost' }), format: 'currency' },
          { key: 'idle_cost', label: translate('reports.table.idleCost', { fallback: 'Idle Cost' }), format: 'currency' },
          { key: 'revenue', label: translate('reports.table.revenue', { fallback: 'Revenue' }), format: 'currency' },
        ],
        summary: {
          title: translate('reports.revenue.title', { fallback: 'Revenue Analysis' }),
          data: [
            {
              label: translate('reports.kpi.totalRevenue', { fallback: 'Total Revenue' }),
              value: formatCurrency(data.summary.total_revenue)
            },
            {
              label: translate('reports.revenueSplit.collected'),
              value: formatCurrency(data.summary.charged_revenue)
            },
            {
              label: translate('reports.revenueSplit.vendorBilled'),
              value: formatCurrency(data.summary.vendor_billed_revenue)
            },
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
            {
              label: translate('reports.kpi.avgRevenuePerSession', { fallback: 'Avg Revenue Per Session' }),
              value: formatCurrency(data.summary.avg_revenue_per_session)
            },
            {
              label: translate('reports.kpi.totalEnergyKwh', { fallback: 'Total Energy (kWh)' }),
              value: formatNumber(data.summary.total_energy_kwh, 2)
            },
            {
              label: translate('reports.kpi.totalEnergyCost', { fallback: 'Total Energy Cost' }),
              value: formatCurrency(data.summary.total_energy_cost)
            },
            {
              label: translate('reports.kpi.totalIdleCost', { fallback: 'Total Idle Cost' }),
              value: formatCurrency(data.summary.total_idle_cost)
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  const formatMethodName = (method: string) => reportMethodName(translate, method);

  // Prepare pie chart data
  const pieChartData = useMemo(() => {
    if (!data?.breakdown_by_method) return [];
    return data.breakdown_by_method.map((item) => ({
      name: formatMethodName(item.method),
      value: item.revenue,
      sessions: item.sessions,
    }));
  }, [data?.breakdown_by_method]);

  // Group data by month if periodType is 'month'
  const processedTimeSeriesData = useMemo(() => {
    if (!data?.time_series) return [];

    if (periodType === 'day') {
      return data.time_series;
    }

    // Group by month
    const monthlyData = new Map<string, TimeSeriesData>();

    data.time_series.forEach((item) => {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (monthlyData.has(monthKey)) {
        const existing = monthlyData.get(monthKey)!;
        existing.revenue += item.revenue;
        existing.sessions += item.sessions;
        existing.charged_sessions += item.charged_sessions;
        existing.energy_kwh += item.energy_kwh;
        existing.energy_cost += item.energy_cost;
        existing.idle_cost += item.idle_cost;
      } else {
        monthlyData.set(monthKey, {
          date: monthKey,
          revenue: item.revenue,
          sessions: item.sessions,
          charged_sessions: item.charged_sessions,
          energy_kwh: item.energy_kwh,
          energy_cost: item.energy_cost,
          idle_cost: item.idle_cost,
        });
      }
    });

    return Array.from(monthlyData.values());
  }, [data?.time_series, periodType]);

  // Prepare sorted table data
  const sortedTableData = useMemo(() => {
    if (!processedTimeSeriesData.length) return [];

    const comparator = (a: TimeSeriesData, b: TimeSeriesData) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (orderBy === 'date') {
        return order === 'asc'
          ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
          : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
      }

      return order === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    };

    return [...processedTimeSeriesData].sort(comparator);
  }, [processedTimeSeriesData, order, orderBy]);

  const topSiteData = useMemo(() => {
    if (!data?.breakdown_by_site) return [];
    return isMobile ? data.breakdown_by_site.slice(0, 5) : data.breakdown_by_site;
  }, [data?.breakdown_by_site, isMobile]);

  return (
    <ReportLayout
      title={translate('reports.revenue.title', { fallback: 'Revenue Analysis' })}
      description={translate('reports.revenue.subtitle', { fallback: 'Revenue performance and financial metrics' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          siteId={siteId}
          onSiteIdChange={setSiteId}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          chargerId={chargerId}
          onChargerIdChange={setChargerId}
          onRefresh={refetch}
          showSiteFilter={true}
          showVendorFilter={true}
          showChargerFilter={true}
          showGroupByFilter={false}
          sites={sites}
          vendors={vendors}
          chargers={chargers}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs for Charts vs Data Table */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, borderBottom: 1, borderColor: 'divider', px: { xs: 1.5, sm: 3 }, pt: 2, minWidth: 0 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="report view tabs"
                  variant={isMobile ? "fullWidth" : "scrollable"}
                  scrollButtons={isMobile ? false : "auto"}
                  sx={{
                    minWidth: 0,
                    minHeight: { xs: 40, sm: 48 },
                    '& .MuiTab-root': {
                      minWidth: { xs: 0, sm: 90 },
                      minHeight: { xs: 40, sm: 48 },
                      px: { xs: 0.5, sm: 2 },
                      py: { xs: 0.75, sm: 1.5 },
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      textTransform: 'none',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    },
                  }}
                >
                  <Tab
                    icon={<TableChart />}
                    iconPosition="start"
                    label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })}
                  />
                  <Tab
                    icon={<ShowChart />}
                    iconPosition="start"
                    label={translate('reports.tabs.charts', { fallback: 'Charts' })}
                  />
                </Tabs>

                {/* Period Type Toggle */}
                <ToggleButtonGroup
                  value={periodType}
                  exclusive
                  onChange={handlePeriodTypeChange}
                  size="small"
                  aria-label="period type"
                  sx={{
                    mb: 1,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      flex: { xs: 1, sm: 'unset' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 0.5 },
                    },
                  }}
                >
                  <ToggleButton value="day" aria-label="daily view">
                    {translate('reports.groupBy.day', { fallback: 'Day' })}
                  </ToggleButton>
                  <ToggleButton value="month" aria-label="monthly view">
                    {translate('reports.groupBy.month', { fallback: 'Month' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Data Table Tab - Now First (index 0) */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.dailyBreakdown', { fallback: periodType === 'month' ? 'Monthly Breakdown' : 'Daily Breakdown' })}
                  </Typography>
                  {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Paper variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                          {translate('reports.table.total', { fallback: 'Total' })}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(data.summary.total_sessions)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(data.summary.charged_sessions)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.avgPerSession', { fallback: 'Avg/Session' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(data.summary.avg_revenue_per_session)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(data.summary.total_energy_kwh, 2)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.energyCost', { fallback: 'Energy Cost' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(data.summary.total_energy_cost)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.idleCost', { fallback: 'Idle Cost' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(data.summary.total_idle_cost)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.revenue', { fallback: 'Revenue' })}</Typography>
                            <Typography sx={{ ...mobileValueSx, color: '#009A75', fontWeight: 700 }}>
                              {formatCurrency(data.summary.total_revenue)}
                            </Typography>
                            <RevenueSplit charged={data.summary.charged_revenue} vendorBilled={data.summary.vendor_billed_revenue} formatCurrency={formatCurrency} />
                          </Box>
                        </Box>
                      </Paper>

                      {sortedTableData.map((row) => {
                        const avgPerSession = row.sessions > 0 ? row.revenue / row.sessions : 0;
                        return (
                          <Paper key={row.date} variant="outlined" sx={mobileRowCardSx}>
                            <Typography sx={{ ...mobileLabelSx, mb: 0.75 }}>
                              {periodType === 'month' ? translate('reports.table.date', { fallback: 'Month' }) : translate('reports.table.date', { fallback: 'Date' })}
                            </Typography>
                            <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 1 }}>
                              {periodType === 'month' ? row.date : formatDate(row.date, 'long')}
                            </Typography>

                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                              {canSeeTotalSessions && (
                                <Box>
                                  <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                                  <Typography sx={mobileValueSx}>{formatNumber(row.sessions)}</Typography>
                                </Box>
                              )}
                              <Box>
                                <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                                <Typography sx={mobileValueSx}>{formatNumber(row.charged_sessions)}</Typography>
                              </Box>
                              <Box>
                                <Typography sx={mobileLabelSx}>{translate('reports.table.avgPerSession', { fallback: 'Avg/Session' })}</Typography>
                                <Typography sx={mobileValueSx}>{formatCurrency(avgPerSession)}</Typography>
                              </Box>
                              <Box>
                                <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                                <Typography sx={mobileValueSx}>{formatNumber(row.energy_kwh, 2)}</Typography>
                              </Box>
                              <Box>
                                <Typography sx={mobileLabelSx}>{translate('reports.table.energyCost', { fallback: 'Energy Cost' })}</Typography>
                                <Typography sx={mobileValueSx}>{formatCurrency(row.energy_cost)}</Typography>
                              </Box>
                              <Box>
                                <Typography sx={mobileLabelSx}>{translate('reports.table.idleCost', { fallback: 'Idle Cost' })}</Typography>
                                <Typography sx={mobileValueSx}>{formatCurrency(row.idle_cost)}</Typography>
                              </Box>
                              <Box>
                                <Typography sx={mobileLabelSx}>{translate('reports.table.revenue', { fallback: 'Revenue' })}</Typography>
                                <Typography sx={{ ...mobileValueSx, color: '#009A75', fontWeight: 700 }}>
                                  {formatCurrency(row.revenue)}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                        <Table stickyHeader sx={tableDetailsSx}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                              <TableCell>
                                <TableSortLabel
                                  active={orderBy === 'date'}
                                  direction={orderBy === 'date' ? order : 'asc'}
                                  onClick={() => handleRequestSort('date')}
                                >
                                  {translate('reports.table.date', { fallback: periodType === 'month' ? 'Month' : 'Date' })}
                                </TableSortLabel>
                              </TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">
                                  <TableSortLabel
                                    active={orderBy === 'sessions'}
                                    direction={orderBy === 'sessions' ? order : 'asc'}
                                    onClick={() => handleRequestSort('sessions')}
                                  >
                                    {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                  </TableSortLabel>
                                </TableCell>
                              )}
                              <TableCell align="right">
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <TableSortLabel
                                    active={orderBy === 'charged_sessions'}
                                    direction={orderBy === 'charged_sessions' ? order : 'asc'}
                                    onClick={() => handleRequestSort('charged_sessions')}
                                  >
                                    {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                  </TableSortLabel>
                                  <MuiTooltip
                                    title={translate('reports.kpi.chargedSessionsTooltip', {
                                      fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                    })}
                                    arrow
                                    placement="top"
                                  >
                                    <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                  </MuiTooltip>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {translate('reports.table.avgPerSession', { fallback: 'Avg/Session' })}
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'energy_kwh'}
                                  direction={orderBy === 'energy_kwh' ? order : 'asc'}
                                  onClick={() => handleRequestSort('energy_kwh')}
                                >
                                  {translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'energy_cost'}
                                  direction={orderBy === 'energy_cost' ? order : 'asc'}
                                  onClick={() => handleRequestSort('energy_cost')}
                                >
                                  {translate('reports.table.energyCost', { fallback: 'Energy Cost' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'idle_cost'}
                                  direction={orderBy === 'idle_cost' ? order : 'asc'}
                                  onClick={() => handleRequestSort('idle_cost')}
                                >
                                  {translate('reports.table.idleCost', { fallback: 'Idle Cost' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'revenue'}
                                  direction={orderBy === 'revenue' ? order : 'asc'}
                                  onClick={() => handleRequestSort('revenue')}
                                >
                                  {translate('reports.table.revenue', { fallback: 'Revenue' })}
                                </TableSortLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedTableData.map((row) => {
                              const avgPerSession = row.sessions > 0 ? row.revenue / row.sessions : 0;
                              return (
                                <TableRow key={row.date} hover>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {periodType === 'month' ? row.date : formatDate(row.date, 'long')}
                                    </Typography>
                                  </TableCell>
                                  {canSeeTotalSessions && (
                                    <TableCell align="right">{formatNumber(row.sessions)}</TableCell>
                                  )}
                                  <TableCell align="right">{formatNumber(row.charged_sessions)}</TableCell>
                                  <TableCell align="right">{formatCurrency(avgPerSession)}</TableCell>
                                  <TableCell align="right">{formatNumber(row.energy_kwh, 2)}</TableCell>
                                  <TableCell align="right">{formatCurrency(row.energy_cost)}</TableCell>
                                  <TableCell align="right">{formatCurrency(row.idle_cost)}</TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#009A75' }}>
                                      {formatCurrency(row.revenue)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                          <TableFooter>
                            <TableRow sx={{
                              backgroundColor: '#F5F5F5',
                              fontWeight: 600,
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                zIndex: 2,
                              }
                            }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {translate('reports.table.total', { fallback: 'Total' })}
                                </Typography>
                              </TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {formatNumber(data.summary.total_sessions)}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatNumber(data.summary.charged_sessions)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(data.summary.avg_revenue_per_session)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatNumber(data.summary.total_energy_kwh, 2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(data.summary.total_energy_cost)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(data.summary.total_idle_cost)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#009A75' }}>
                                  {formatCurrency(data.summary.total_revenue)}
                                </Typography>
                                <RevenueSplit charged={data.summary.charged_revenue} vendorBilled={data.summary.vendor_billed_revenue} formatCurrency={formatCurrency} />
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              {/* Charts Tab - Now Second (index 1) */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                  {/* Revenue Over Time Chart */}
                  <Box sx={{ mb: 3, p: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', borderRadius: 2, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.revenueOverTime', { fallback: 'Revenue Over Time' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                      <LineChart data={processedTimeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(date) => periodType === 'month' ? date : formatDate(date)}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          interval={isMobile ? 'preserveStartEnd' : 0}
                          minTickGap={isMobile ? 24 : 12}
                        />
                        <YAxis
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                          width={isMobile ? 42 : 60}
                        />
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          labelFormatter={(date) => periodType === 'month' ? date : formatDate(date, 'long')}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                        <Line
                          type="monotone"
                          dataKey="revenue"
                          stroke="#009A75"
                          strokeWidth={isMobile ? 2 : 3}
                          dot={{ r: isMobile ? 3 : 4, fill: '#009A75' }}
                          name="Revenue"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, minWidth: 0 }}>
                    {/* Revenue by Site Chart */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', borderRadius: 2, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#1A1A1A',
                          mb: 3,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {translate('reports.charts.revenueBySite', { fallback: 'Revenue by Site (Top 10)' })}
                      </Typography>
                      {topSiteData && topSiteData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
                          {isMobile ? (
                            <BarChart
                              data={topSiteData}
                              layout="vertical"
                              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 10 }}
                                stroke="#606060"
                                tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                              />
                              <YAxis
                                type="category"
                                dataKey="site_name"
                                width={90}
                                tick={{ fontSize: 10 }}
                                stroke="#606060"
                                tickFormatter={(value) => String(value).slice(0, 14)}
                              />
                              <Tooltip
                                formatter={(value: number) => formatCurrency(value)}
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E0E0E0',
                                  borderRadius: 8,
                                }}
                              />
                              <Bar dataKey="revenue" fill="#009A75" radius={[0, 6, 6, 0]} />
                            </BarChart>
                          ) : (
                            <BarChart data={topSiteData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                              <XAxis
                                dataKey="site_name"
                                angle={-45}
                                textAnchor="end"
                                height={100}
                                tick={{ fontSize: 11 }}
                                stroke="#606060"
                                interval={0}
                              />
                              <YAxis
                                tick={{ fontSize: 12 }}
                                stroke="#606060"
                                tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                              />
                              <Tooltip
                                formatter={(value: number) => formatCurrency(value)}
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E0E0E0',
                                  borderRadius: 8,
                                }}
                              />
                              <Bar dataKey="revenue" fill="#009A75" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ py: 8, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No site data available
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Revenue by Initiation Method Chart */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', borderRadius: 2, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#1A1A1A',
                          mb: 3,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {translate('reports.charts.revenueByMethod', { fallback: 'Revenue by Initiation Method' })}
                      </Typography>
                      <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={isMobile ? 70 : 80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Legend
                            verticalAlign={isMobile ? 'bottom' : 'top'}
                            align="center"
                            wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Box>
              </TabPanel>
            </CardContent>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
